import { Component, createRef } from 'react'
import Input from '~/components/OrdersPage/Input'
import LbOzInput from '~/components/OrdersPage/LbOzInput'
import Switch from '~/components/Switch/Switch'
import Package from '~/components/OrdersPage/Package'
import Points from '~/utils/Points'
import Measurements from '~/utils/Measurements'
import styles from '~/styles/ordersPage.someOrderProperties.module.css'
import types from '~/types/ordersPage.ts'

export default class SomeOrderProperties extends Component {
	static getInitialProps() {
		return {
			namespacesRequired: [ 'Orders', 'common' ],
		}
	}
	
	constructor(props) {
		super(props)
		this._name = 'SomeOrderProperties'
		this.state = {
			chargeableWeight: null,
			packageCost: 0,
			packageCode: null,
		}
		this.props.setChargeableWeightUpdater(this._updateChargeableWeight.bind(this))
		this.props.setShipperUpdater(this._setShipperUpdater.bind(this))
		this._currencySign = this.props.t((this.props.order?.currency || 'USD') + ' Sign')
		setTimeout(() => {
			this._updateChargeableWeight(null, this.props.order?.packageObject?.package_code)
		})
		this._setPackage = null
	}
	
	render(): JSX.Element {
		const priceTitle = this.props.t('Order price') + ', ' + this._currencySign
		const isNoItems = !!(this.props.order?.hasDefaultItem && Object.keys(this.props.order?.orderItems || {}).length === 1)
		const isPaid = this.props.order?.paid
		const isTotalPriceDisabled = !(isNoItems && isPaid)
		let autoPackageClass = styles.autoPackage
		if (!this.state.autoPackageName || this.props.order.packageObject) {
			autoPackageClass += ' ' + styles.hidden
		}
		return (
			<div
				className={styles.main + ((this.props.order.onEdit && this.props.order.paid) ? ' ' + styles.paidOrderWarning : '')}
			>
				<div
					className={styles.finance}
				>
					<div
						className={styles.price}
					>
						<Input
							label={priceTitle}
							initial={this.props.order?.in_total || 0}
							placeholder="0"
							isRequiredFunction={() => !(this.props.order?.in_total >= 0)}
							onChange={this._onTotalPriceChange.bind(this, this.props.editTheOrder.bind(this, 'in_total'))}
							min="0"
							step="0.01"
							widthRem="7"
							disabled={isTotalPriceDisabled}
							t={this.props.t}
						/>
					</div>
					{this._showPaid()}
				</div>
				<div
					className={styles.packageFrame}
				>
					<div
						className={styles.package}
					>
						<div>
							<Package
								dealerId={this.props.order.dealerId}
								order={this.props.order}
								editTheOrder={this.props.editTheOrder}
								inputSetter={this._inputSetter}
								updateChargeableWeight={this._updateChargeableWeight.bind(this)}
								refreshPointPrice={this._refreshPointPrice.bind(this)}
								calculatedPackageCode={this.state.packageCode}
								removeCalculatedPackageCode={this._setPackageCode.bind(this, null)}
								setPackageInit={this._setPackageInit.bind(this)}
								isRu={this.props.isRu}
								t={this.props.t}
							/>
						</div>
						<div
							className={autoPackageClass}
						>
							<span>
								{this.props.t('Auto-selected package') + ': '}
							</span>
							<span>
								{this.state.autoPackageName}
							</span>
						</div>							
					</div>
					<div
						className={styles.dimensionsAndWeight}
					>
						<div
							className={styles.dimensions}
						>
							{this._showPackageDimensions()}
						</div>
						<div
							className={styles.chargeableWeightBeakFrame}
						>
							<div/>
						</div>
						<div
							className={styles.weight}
						>
							{this._showPackageWeight()}
						</div>
						<div
							className={styles.chargeableWeightFrame}
						>
							<div>
								<div/>
							</div>
							<div>
								{this._getDimensionsWeightUnderLine()}
							</div>	
						</div>
					</div>
				</div>
			</div>
		)
	}

	private _setPackageCost(packageCost: number): void {
		this.setState({ packageCost })
	}

	private _setPackageCode(packageCode: string): void {
		if (this._setPackage) {
			this._setPackage(packageCode)
		}
		this.setState({ packageCode })
	}

	private _setPackageInit(callback): void {
		this._setPackage = callback
	}

	private _getDimensionsWeightUnderLine(): JSX.Element {
		return (
			<>
				<span>
					{this.props.t('Chargeable weight') + ': '}
				</span>
				<span
					className={styles.dimensionsWeightUnderLineBold}
				>
					{this._getChargeableWeightString()}
				</span>
				<span>
					{', ' + this.props.t('package cost') + ': '}
				</span>
				<span
					className={styles.dimensionsWeightUnderLineBold}
				>
					{this._getPackageCostString()}
				</span>
			</>
		)
	}

	private _getChargeableWeightString(): string {
		let chargeableWeightString = ''
		if (this.state.chargeableWeight === 'locationNotSelected') {
			chargeableWeightString = this.props.t('(location not selected)')
		} else {
			let chargeableWeightGr = this.state.chargeableWeight
			if (!this.state.chargeableWeight) {
				chargeableWeightGr = this.props.order?.in_weight * 1000 || this.props.order.defaultPackageWeightGr
			}
			if (!this._isUsMeasure(true)) {
				chargeableWeightString = ((this.state.chargeableWeight?.kg * 1000) || chargeableWeightGr) + ' ' + this.props.t('shortForGram')
			} else {
				chargeableWeightString = this._getRoundLbOz(this.state.chargeableWeight?.lb, chargeableWeightGr)
			}
		}
		return chargeableWeightString
	}

	private _getPackageCostString(): string {
		let packageCost = this.state.packageCost || this.state.autoPackagePrice || 0
		if (typeof packageCost === 'number') {
			packageCost = packageCost.toFixed(2)
		} else if (typeof packageCost === 'text') {
			packageCost = parseFloat(packageCost).toFixed(2)
		}
		if (this.props.order.currency === 'RUB') {
			packageCost += '\u202F' + this._currencySign
		} else {
			packageCost = this._currencySign + packageCost
		}
		return packageCost
	}

	private _getRoundLbOz(decimalLb?: number, weightGr: number) {
		if (!decimalLb) {
			decimalLb = (weightGr * 2.20462262185) / 1000
		}
		let lb = Math.trunc(decimalLb)
		let oz = Math.round((decimalLb % 1) * 16)
		if (oz === 16) {
			lb += 1
			oz = 0
		}
		return `${lb} lb ${oz} oz`
	}
	
	private _onTotalPriceChange(callback, value): void {
		this._onWarningChange(callback, value)
		this._refreshPointPrice()
	}
	
	private _onWarningChange(callback, value): void {
		if (this.props.order.onEdit && this.props.order.paid) {
			this.props.onWarningChange(callback, value)
		} else {
			callback(value)
		}
	}
	
	private _showPaid(): JSX.Element {
		const dealerCountry = this.props?.order?.dealer_country
		const enCountries = ['US', 'GB', 'UK', 'CA']
		if (enCountries.includes(dealerCountry)) {
			return null
		} else {
			return (
				<div
					className={styles.paidFrame}
				>
					<div
						className={styles.paid}
					>
						<label>
							{this.props.t('paid_label')}
						</label>
						<div>
							<Switch
								isOn={!!this.props.order.paid}
								onChange={this._onWarningChange.bind(this, this.props.editTheOrder.bind(this, 'paid'))}
							/>
						</div>
					</div>
				</div>
			)
		}
	}

	private _inputSetterInit(type, callback) {
		if (!this._inputSetter) {
			this._inputSetter = {}
		}
		this._inputSetter[type] = callback
	}
	
	private _showPackageDimensions(): JSX.Element {
		const isUsMeasure = this._isUsMeasure()
		return (
			<>
				<Input
					label={isUsMeasure ? this.props.t('Length, in') : this.props.t('Length, cm')}
					initial={this.props.order.mu_in_length}
					valueSetterInit={this._inputSetterInit.bind(this, 'in_length')}
					onChange={this._convertToMm.bind(this, 'in_length')}
					step={isUsMeasure ? "0.1" : "1"}
					min="0"
					placeholder={this.props.order.defaultPackageLengthMu || 0}
					isRequiredFunction={() => !(this._getDimension('in_length') > 0)}
					isRequiredSignInside="true"
					widthRem="5"
					focused="true"
					isRu={this.props.isRu}
					t={this.props.t}
				/>
				<Input
					label={isUsMeasure ? this.props.t('Width, in') : this.props.t('Width, cm')}
					initial={this.props.order.mu_in_width}
					valueSetterInit={this._inputSetterInit.bind(this, 'in_width')}
					onChange={this._convertToMm.bind(this, 'in_width')}
					step={isUsMeasure ? "0.1" : "1"}
					min="0"
					placeholder={this.props.order.defaultPackageWidthMu || 0}
					isRequiredFunction={() => !(this._getDimension('in_width') > 0)}
					isRequiredSignInside="true"
					widthRem="5"
					marginLeftRem="0.375"
					focused="true"
					isRu={this.props.isRu}
					t={this.props.t}
				/>
				<Input
					label={isUsMeasure ? this.props.t('Height, in') : this.props.t('Height, cm')}
					initial={this.props.order.mu_in_height}
					valueSetterInit={this._inputSetterInit.bind(this, 'in_height')}
					onChange={this._convertToMm.bind(this, 'in_height')}
					step={isUsMeasure ? "0.1" : "1"}
					min="0"
					placeholder={this.props.order.defaultPackageHeightMu || 0}
					isRequiredFunction={() => !(this._getDimension('in_height') > 0)}
					isRequiredSignInside="true"
					widthRem="5"
					marginLeftRem="0.375"
					focused="true"
					isRu={this.props.isRu}
					t={this.props.t}
				/>
			</>
		)
	}
	
	private _showPackageWeight(): JSX.Element {
		if (!this._isUsMeasure(true)) {
			const measurements = new Measurements(null, this.props.order.mu_order_weight)
			const grWeight = measurements.muToGr(this.props.order.mu_in_weight)
			return (
				<Input
					label={this.props.t('Weight, g')}
					initial={Math.round(grWeight)}
					valueSetterInit={this._inputSetterInit.bind(this, 'in_weight')}
					onChange={this._convertToKg.bind(this, 'in_weight')}
					step="1"
					min="0"
					placeholder={this.props.defaultPackageWeightMu || 0}
					isRequiredFunction={() => !this.props.order.in_weight}
					isRequiredSignInside="true"
					widthRem="6"
					focused="true"
					t={this.props.t}
				/>
			)
		} else {
			const measurements = new Measurements(null, this.props.order.mu_order_weight || this.props.order.dealer_weight_mu)
			const weight = measurements.muToLb(this.props.order.mu_in_weight)
			return (
				<div
					className={styles.usWeight}
				>
					<label>
						{this.props.t('Total Weight')}
					</label>
					<LbOzInput
						initial={weight}
						isNotMetrical={true}
						valueSetterInit={this._inputSetterInit.bind(this, 'in_weight')}
						onChange={this._onWeightUs.bind(this)}
						isRequired="true"
						isRequiredSignInside="true"
						isLabeled="true"
						step="1"
						min="0"
						t={this.props.t}
					/>
				</div>
			)
		}
	}
	
	private _onWeightUs(muWeight: number): void {
		this.props.editTheOrder({
			['mu_in_weight']: muWeight,
		})
		this._refreshPointPrice()
		this._setPackageCode(null)
	}
	
	private _getDimension(key: string): number {
		let defaultValue = this.props.order?.defaultPackageLengthMu
		if (key === 'in_weight') {
			defaultValue = this.props.order?.defaultPackageWidthMu
		} else if (key === 'in_height') {
			defaultValue = this.props.order?.defaultPackageHeightMu
		}
		return this.props.order?.['mu_' + key] || defaultValue || 0
	}

	private _isUsMeasure(isWeight: boolean): boolean {
		let isUsMeasure = false
		if (this.props.order?.dealer_dimensions_mu && this.props.order?.dealer_weight_mu) {
			if (isWeight) {
				if (this.props.order?.dealer_weight_mu === 'lb') {
					isUsMeasure = true
				}
			} else {
				if (this.props.order?.dealer_dimensions_mu === 'in') {
					isUsMeasure = true
				}
			}
		} else {
			if (!this.props.isRu) {
				isUsMeasure = true
			}
		}
		return isUsMeasure
	}
	
	private _convertToMm(key: string, value: number): void {
		let valueMm = value
		if (this._isUsMeasure()) {
			valueMm = value * 25.4
		} else {
			valueMm = value * 10
		}
		if (valueMm < 0) {
			valueMm = 0
		}
		if (this.props.editTheOrder) {
			const data = {
				[key]: valueMm,
				['mu_' + key]: value,
			}  
			this.props.editTheOrder(data)
			this._refreshPointPrice()
			this._setPackageCode(null)
		}
	}
	
	private _convertToKg(key: string, value: number): void {
		let valueKg = value / 1000
		if (valueKg < 0) {
			valueKg = 0
		}
		if (this.props.editTheOrder) {
			this.props.editTheOrder({
				['mu_' + key]: valueKg,
			})
			this._refreshPointPrice()
			this._setPackageCode(null)
		}
	}
	
	private _refreshPointPrice(packageCode): void {
		let address = this.props.order.customer_address?.full_address
		if (!address) {
			address = this.props.order.manuallyEnteredCustomerAddress
		}
		this._updateChargeableWeight(null, packageCode)
		if (address?.length) {
	 		this.props.setPointsLoading(true)
	 	}
		if (this._onPointsST) {
			clearTimeout(this._onPointsST)
		}
		this._onPointsST = setTimeout(async () => {
			let newData
			if (address) {
				const points = new Points({
					order: this.props.order,
					choosePointType: 'someOrderPropertiesAreChanged',
					isRu: this.props.isRu,
					packageCode,
				})
				const pointID = this.props.order.in_point_uuid || this.props.order.selectedPointId
				newData = await points.getNearestPoints(address, pointID)
			}
			if (newData && newData?.points?.length) {
				const dimensionalMU = this.props.order.dealer_dimensions_mu
				const weightMU = this.props.order.dealer_weight_mu
				const measurements = new Measurements(dimensionalMU, weightMU)
				const { muLength, muWidth, muHeight, muWeight } = measurements.getByOrder(this.props.order)
				const sameTotal = (newData.totalPrice === this.props.order.in_total)
				const sameLength = (newData.muLength === muLength)
				const sameWidth = (newData.muWidth === muWidth)
				const sameHeight = (newData.muHeight === muHeight)
				const sameWeight = (newData.muWeight === muWeight)
				if (sameTotal && sameLength && sameWidth && sameHeight && sameWeight) {
					this.props?.editTheOrder(newData)
					this.props.setEmptyResult(true)
					setTimeout(() => {
						this.props.setPointsLoading(false)
					})
				} else {
					this._removeLoadingOnFail()
				}
			} else {
				this.props?.editTheOrder('points', [])
				setTimeout(() => {
					this.props.setPointsLoading(false)
				})
			}
		}, 100)
	}

	private _removeLoadingOnFail(): void {
		if (this._removeLoadingOnFailST) {
			clearTimeout(this._removeLoadingOnFailST)
		}
		this._removeLoadingOnFailST = setTimeout(() => {
			this.props.setPointsLoading(false)
		}, 2000)
	}

	private async _updateChargeableWeight(dealerId, packageCode): void {
		const selectedPointId = this.props.order.selectedPointId || this.props.order?.points?.[0]?.id
		let partner = null
		const points = this.props.order?.points || []
		points.some(point => {
			if (point?.id === selectedPointId) {
				partner = point?.partner || null
				return true
			}
		})
		const pointsHandler = new Points({
			order: this.props.order,
			dealerId,
			partner,
			packageCode,
		})
		const weightAndPackage = await pointsHandler.getChargeableWeightAndPackage()
		const weightObject = weightAndPackage?.chargeable_weight_dealer || weightAndPackage?.weight
		const autoPackagePrice = weightAndPackage?.package_details?.package_price
		const toState = { 
			chargeableWeight: null,
			autoPackageName: weightAndPackage?.package_details?.package_name,
			autoPackagePrice,
		}
		if (weightObject?.kg && weightObject?.lb) {
			toState.chargeableWeight = weightObject
		} else {
			if (weightObject?.noPartner) {
				toState.chargeableWeight = 'locationNotSelected'	
			}
		}
		this.setState(toState)
		this._setPackageCost(weightAndPackage?.package_details?.package_price || 0)
		const dimensionalMU = this.props.order.dealer_dimensions_mu
		const weightMU = this.props.order.dealer_weight_mu
		const measurements = new Measurements(dimensionalMU, weightMU)
		let autoPackageDimensions = null
		if (weightAndPackage?.package_details?.package_length) {
			autoPackageDimensions = {
				dmuLength: measurements.muToMu(weightAndPackage?.package_details?.package_length, weightAndPackage?.dim_units),
				dmuWidth: measurements.muToMu(weightAndPackage?.package_details?.package_width, weightAndPackage?.dim_units),
				dmuHeight: measurements.muToMu(weightAndPackage?.package_details?.package_height, weightAndPackage?.dim_units),
			}
		}
		this.props.editTheOrder({
			packageWeightMu: weightAndPackage?.package_details?.package_weight || 0,
			autoPackageCode: weightAndPackage?.package_details?.package_code,
			autoPackageDimensions: autoPackageDimensions,
		})
	}

	private _setShipperUpdater(changes, isByDefault) {
		if (isByDefault) {
			if (changes.defaultPackageLengthMu) {
				const muLength = changes.defaultPackageLengthMu
				const muWidth = changes.defaultPackageWidthMu
				const muHeight = changes.defaultPackageHeightMu
				const muWeight = changes.defaultPackageWeightMu
				this.props.editTheOrder({
					mu_in_length: muLength,
					mu_in_width: muWidth,
					mu_in_height: muHeight,
					mu_in_weight: muWeight,
				})
				this._inputSetter?.in_length?.(muLength)
				this._inputSetter?.in_width?.(muWidth)
				this._inputSetter?.in_height?.(muHeight)
				this._inputSetter?.in_weight?.(muWeight)
			}
		}
	}
}